import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  Timestamp,
  QuerySnapshot,
  DocumentData,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "./AuthContext";
import { ProfileContext } from "./ProfileContext";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";

export type Crew = {
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  avatarUrl?: string;

  schedulerId?: string;
  memberIds?: string[];
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
};

type CrewContextValue = {
  crews: Crew[];
  crewsLoading: boolean;
  crewsError?: string; // optional dev aid
  createCrew: (args: { name: string; description?: string }) => Promise<string>;
  updateCrewMembers: (args: {crewId: string;memberIds: string[];}) => Promise<void>;
  updateCrewDetails: (args: {crewId: string;name: string;description?: string;}) => Promise<void>;
  deleteCrew: (args: { crewId: string }) => Promise<void>;
};

export const CrewContext = createContext<CrewContextValue>({
  crews: [],
  crewsLoading: true,
  crewsError: undefined,
  createCrew: async () => {
    throw new Error("CrewContextProvider not mounted");
  },
  updateCrewMembers: async () => {
    throw new Error("CrewContextProvider not mounted");
  },
  updateCrewDetails: async () => {
    throw new Error("CrewContextProvider not mounted");
  },
    deleteCrew: async () => {
    throw new Error("CrewContextProvider not mounted");
  },
});

export const CrewContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, authReady } = useContext(AuthContext);
  const { profile } = useContext(ProfileContext);

  const canSeeCrews = !!profile?.UserLastRoleScheduler;

  const [crews, setCrews] = useState<Crew[]>([]);
  const [crewsLoading, setCrewsLoading] = useState<boolean>(true);
  const [crewsError, setCrewsError] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Reset when auth/profile changes
    if (!authReady || !currentUser?.uid || currentUser.uid === "") {
      setCrews([]);
      setCrewsLoading(false);
      setCrewsError(undefined);
      return;
    }

    // Only subscribe in Scheduler mode (your rule)
    if (!canSeeCrews) {
      setCrews([]);
      setCrewsLoading(false);
      setCrewsError(undefined);
      return;
    }

    setCrewsLoading(true);
    setCrewsError(undefined);

    const crewsRef = collection(db, FB_COLLECTION_PATH.Crews);

    // Avoid index requirements during scaffolding:
    const q = query(crewsRef, where("SchedulerId", "==", currentUser.uid));

    const unsub = onSnapshot(
      q,
      (snap: QuerySnapshot<DocumentData>) => {
        const next: Crew[] = snap.docs.map((d) => {
          const data = d.data() || {};

          const avatarUrl = (data.CrewAvatarUrl || "").toString();
          const name = (data.CrewName || "").toString();
          const description = (data.CrewDescription || "").toString();
          const memberIds = Array.isArray(data.CrewMemberIds)
            ? (data.CrewMemberIds as string[])
            : [];

          return {
            id: d.id,
            name,
            description,
            memberCount: memberIds.length,
            avatarUrl: avatarUrl || undefined,
            schedulerId: data.SchedulerId,
            memberIds,
            createdAt: data.CreatedAt ?? null,
            updatedAt: data.UpdatedAt ?? null,
          };
        });

        // Client-side sort (keeps list stable without Firestore orderBy/index)
        next.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        setCrews(next);
        setCrewsLoading(false);
        setCrewsError(undefined);
      },
      (err) => {
        console.error("CrewContext onSnapshot error:", err);
        setCrews([]);
        setCrewsLoading(false);
        setCrewsError(err?.message || "Unknown error");
      }
    );

    return () => unsub();
  }, [authReady, currentUser?.uid, canSeeCrews]);

  const createCrew = useCallback(
    async (args: { name: string; description?: string }) => {
      const name = (args.name || "").trim();
      const description = (args.description || "").trim();

      if (!authReady || !currentUser?.uid || currentUser.uid === "") {
        throw new Error("Not authenticated.");
      }

      // Must be in scheduler mode to create crews
      if (!canSeeCrews) {
        throw new Error("Not in Scheduler mode.");
      }

      if (!name) {
        throw new Error("Crew name is required.");
      }

      const crewsRef = collection(db, FB_COLLECTION_PATH.Crews);

      const docRef = await addDoc(crewsRef, {
        SchedulerId: currentUser.uid,
        CrewName: name,
        CrewDescription: description,
        CrewMemberIds: [],
        CrewAvatarUrl: "",
        CreatedAt: serverTimestamp(),
        UpdatedAt: serverTimestamp(),
      });

      return docRef.id;
    },
    [authReady, currentUser?.uid, canSeeCrews]
  );

  //   Update Crew Members ///////////////////////////////////////////////
  const updateCrewMembers = useCallback(
    async (args: { crewId: string; memberIds: string[] }) => {
      if (!authReady || !currentUser?.uid || currentUser.uid === "") {
        throw new Error("Not authenticated.");
      }

      if (!canSeeCrews) {
        throw new Error("Not in Scheduler mode.");
      }

      const crewId = (args.crewId || "").trim();
      if (!crewId) {
        throw new Error("Crew id missing.");
      }

      const memberIds = Array.isArray(args.memberIds) ? args.memberIds : [];

      const ref = doc(db, FB_COLLECTION_PATH.Crews, crewId);

      await updateDoc(ref, {
        CrewMemberIds: memberIds,
        UpdatedAt: serverTimestamp(),
      });
    },
    [authReady, currentUser?.uid, canSeeCrews]
  );

  //   Update Crew Details ///////////////////////////////////////////////
  const updateCrewDetails = useCallback(
    async (args: { crewId: string; name: string; description?: string }) => {
      if (!authReady || !currentUser?.uid || currentUser.uid === "") {
        throw new Error("Not authenticated.");
      }

      if (!canSeeCrews) {
        throw new Error("Not in Scheduler mode.");
      }

      const crewId = (args.crewId || "").trim();
      if (!crewId) {
        throw new Error("Crew id missing.");
      }

      const name = (args.name || "").trim();
      if (!name) {
        throw new Error("Crew name is required.");
      }

      const description = (args.description || "").trim();

      const ref = doc(db, FB_COLLECTION_PATH.Crews, crewId);

      await updateDoc(ref, {
        CrewName: name,
        CrewDescription: description,
        UpdatedAt: serverTimestamp(),
      });
    },
    [authReady, currentUser?.uid, canSeeCrews]
  );

    // Delete Crew ///////////////////////////////////////////////
  const deleteCrew = useCallback(
    async (args: { crewId: string }) => {
      if (!authReady || !currentUser?.uid || currentUser.uid === "") {
        throw new Error("Not authenticated.");
      }

      if (!canSeeCrews) {
        throw new Error("Not in Scheduler mode.");
      }

      const crewId = (args.crewId || "").trim();
      if (!crewId) {
        throw new Error("Crew id missing.");
      }

      const ref = doc(db, FB_COLLECTION_PATH.Crews, crewId);

      // NOTE: Security rules should enforce SchedulerId ownership.
      await deleteDoc(ref);
    },
    [authReady, currentUser?.uid, canSeeCrews]
  );


  const value = useMemo(() => {
    return { crews, crewsLoading, crewsError, createCrew, updateCrewMembers, updateCrewDetails, deleteCrew};
  }, [crews, crewsLoading, crewsError, createCrew, updateCrewMembers, updateCrewDetails, deleteCrew]);

  return <CrewContext.Provider value={value}>{children}</CrewContext.Provider>;
};
